import * as pc from 'playcanvas';
import { ColyseusClient } from './network/ColyseusClient';
import { InputSystem, type InputState } from './systems/InputSystem';
import { TouchInput } from './systems/TouchInput';
import { CameraSystem } from './systems/CameraSystem';
import { PlayerController } from './systems/PlayerController';
import { ParticleSystem } from './systems/ParticleSystem';
import { MachineEntity } from './entities/MachineEntity';
import { BulletPool } from './entities/Bullet';
import { DestructibleEntity } from './entities/Destructible';
import { PowerUpEntity } from './entities/PowerUpEntity';
import { PortalEntity } from './entities/PortalEntity';
import { HUD } from './ui/HUD';
import { Leaderboard } from './ui/Leaderboard';
import { GlobalLeaderboard } from './ui/GlobalLeaderboard';
import { CTFHud } from './ui/CTFHud';
import { FlagEntity } from './entities/FlagEntity';
import { DESTRUCTIBLE_DEFS, UPGRADE_DEFS, type DestructibleType, type PowerUpType, CTF_RED_FLAG_HOME, CTF_BLUE_FLAG_HOME } from './shared';
import { WALL_DEFS } from './wallDefs';
import { AudioSystem } from './systems/AudioSystem';
import { DebugSystem } from './systems/DebugSystem';
import { HelpWindow } from './ui/HelpWindow';
import { BloomEffect } from './posteffects/BloomEffect';

// ── Loading helpers ─────────────────────────────────────────
const loadingEl   = document.getElementById('loading')!;
const loadingBar  = document.getElementById('loading-bar-fill') as HTMLDivElement;
const loadingText = document.getElementById('loading-status')!;
const nameScreen  = document.getElementById('name-screen')!;
const nameInput   = document.getElementById('name-input') as HTMLInputElement;
const enterBtn    = document.getElementById('enter-btn')!;
const modeScreen  = document.getElementById('mode-screen')!;
const modeFfaBtn  = document.getElementById('mode-ffa')!;
const modeCtfBtn  = document.getElementById('mode-ctf')!;

// Lobby screen elements
const lobbyScreen       = document.getElementById('lobby-screen')!;
const lobbyTitle        = document.getElementById('lobby-title')!;
const lobbyQuickplayBtn = document.getElementById('lobby-quickplay-btn')!;
const lobbyCreateBtn    = document.getElementById('lobby-create-btn')!;
const lobbyCodeInput    = document.getElementById('lobby-code-input') as HTMLInputElement;
const lobbyJoinBtn      = document.getElementById('lobby-join-btn')!;
const lobbyTeamPicker   = document.getElementById('lobby-team-picker')!;
const lobbyTeamRed      = document.getElementById('lobby-team-red')!;
const lobbyTeamBlue     = document.getElementById('lobby-team-blue')!;
const lobbyBackBtn      = document.getElementById('lobby-back-btn')!;
// Waiting room elements
const waitingRoom       = document.getElementById('waiting-room')!;
const waitingRoomCode   = document.getElementById('waiting-room-code')!;
const waitingRoomCopy   = document.getElementById('waiting-room-copy-btn')!;
const waitingRoomPlayers = document.getElementById('waiting-room-players')!;
const waitingRoomTeams  = document.getElementById('waiting-room-teams')!;
const wrTeamRed         = document.getElementById('wr-team-red')!;
const wrTeamBlue        = document.getElementById('wr-team-blue')!;
const waitingRoomStart  = document.getElementById('waiting-room-start-btn') as HTMLButtonElement;
const waitingRoomBack   = document.getElementById('waiting-room-back-btn')!;
const waitingRoomStatus = document.getElementById('waiting-room-status')!;
const waitingRoomTitle  = document.getElementById('waiting-room-title')!;
const waitingRoomDuration = document.getElementById('waiting-room-duration')!;
const wrDurationInput   = document.getElementById('wr-duration-input') as HTMLInputElement;

function setLoadProgress(pct: number, label: string) {
  loadingBar.style.width = `${pct}%`;
  loadingText.textContent = label;
}

// ── Checkerboard texture (procedural, no external files) ─────
function makeCheckerTexture(device: pc.GraphicsDevice): pc.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 512;
  const ctx = c.getContext('2d')!;
  // Color A: base dark
  ctx.fillStyle = '#1e1e22';
  ctx.fillRect(0, 0, 512, 512);
  // Color B: slightly darker checker squares (top-right + bottom-left quadrants)
  ctx.fillStyle = '#171719';
  ctx.fillRect(256, 0, 256, 256);
  ctx.fillRect(0, 256, 256, 256);
  const tex = new pc.Texture(device, {
    width: 512, height: 512,
    format: pc.PIXELFORMAT_RGBA8,
    addressU: pc.ADDRESS_REPEAT,
    addressV: pc.ADDRESS_REPEAT,
    minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR,
    magFilter: pc.FILTER_LINEAR,
    mipmaps: true,
  });
  tex.setSource(c);
  return tex;
}

// ── Sky gradient texture (procedural) ────────────────────────
function makeSkyGradientTexture(device: pc.GraphicsDevice): pc.Texture {
  const c = document.createElement('canvas');
  c.width = 1;
  c.height = 256;
  const ctx = c.getContext('2d')!;
  const grad = ctx.createLinearGradient(0, 0, 0, 256);
  grad.addColorStop(0, '#0e0e20');
  grad.addColorStop(0.5, '#0a0a18');
  grad.addColorStop(1, '#060610');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1, 256);
  const tex = new pc.Texture(device, {
    width: 1, height: 256,
    format: pc.PIXELFORMAT_RGBA8,
    addressU: pc.ADDRESS_CLAMP_TO_EDGE,
    addressV: pc.ADDRESS_CLAMP_TO_EDGE,
    minFilter: pc.FILTER_LINEAR,
    magFilter: pc.FILTER_LINEAR,
    mipmaps: false,
  });
  tex.setSource(c);
  return tex;
}

function buildSkyDome(app: pc.Application) {
  const sky = new pc.Entity('sky');
  sky.addComponent('render', { type: 'sphere' });
  sky.setLocalScale(400, 400, 400);

  const skyMat = new pc.StandardMaterial();
  skyMat.diffuse = new pc.Color(0, 0, 0);
  skyMat.emissive = new pc.Color(1, 1, 1);
  skyMat.emissiveIntensity = 1.0;
  skyMat.emissiveMap = makeSkyGradientTexture(app.graphicsDevice);
  skyMat.cull = pc.CULLFACE_FRONT;
  skyMat.depthWrite = false;
  skyMat.useFog = false;
  skyMat.update();

  sky.render!.meshInstances[0].material = skyMat;
  sky.render!.castShadows = false;
  sky.render!.receiveShadows = false;
  app.root.addChild(sky);
}

// ── Build arena floor ────────────────────────────────────────
function buildArena(app: pc.Application) {
  // Static batch group — collapses all non-moving arena geometry into minimal draw calls
  const BATCH_ARENA = app.batcher.addGroup('arena', false, 500).id;

  // Single ground plane with tiled checkerboard texture (replaces 1,250 tile entities)
  const ground = new pc.Entity('ground');
  ground.addComponent('render', { type: 'plane' });
  const groundMat = new pc.StandardMaterial();
  groundMat.diffuseMap = makeCheckerTexture(app.graphicsDevice);
  groundMat.diffuseMapTiling = new pc.Vec2(50, 50);
  groundMat.update();
  ground.render!.meshInstances[0].material = groundMat;
  ground.render!.castShadows = false;
  ground.render!.batchGroupId = BATCH_ARENA;
  ground.setLocalScale(200, 1, 200);
  app.root.addChild(ground);

  // Arena boundary walls
  const arenaSize = 98;
  const boundaryDefs = [
    { x: 0,          z: -arenaSize },
    { x: 0,          z:  arenaSize },
    { x: -arenaSize, z: 0,         },
    { x:  arenaSize, z: 0,         },
  ];
  const boundaryScales = [
    [200, 3, 1.5],
    [200, 3, 1.5],
    [1.5, 3, 200],
    [1.5, 3, 200],
  ];

  const boundaryMat = new pc.StandardMaterial();
  boundaryMat.diffuse = new pc.Color(0.18, 0.18, 0.24);
  boundaryMat.emissive = new pc.Color(0.08, 0.08, 0.18);
  boundaryMat.emissiveIntensity = 0.6;
  boundaryMat.update();

  boundaryDefs.forEach((w, i) => {
    const wall = new pc.Entity(`wall_${i}`);
    wall.addComponent('render', { type: 'box' });
    wall.render!.meshInstances[0].material = boundaryMat;
    wall.render!.batchGroupId = BATCH_ARENA;
    wall.setLocalScale(...boundaryScales[i] as [number, number, number]);
    wall.setPosition(w.x, 1.5, w.z);
    app.root.addChild(wall);
  });

  // Interior walls — match collision boxes defined in wallDefs.ts
  const wallHeight = 3;
  const interiorMat = new pc.StandardMaterial();
  interiorMat.diffuse = new pc.Color(0.20, 0.20, 0.26);
  interiorMat.emissive = new pc.Color(0.07, 0.07, 0.16);
  interiorMat.emissiveIntensity = 0.5;
  interiorMat.update();

  for (const w of WALL_DEFS) {
    const wall = new pc.Entity('interior_wall');
    wall.addComponent('render', { type: 'box' });
    wall.render!.meshInstances[0].material = interiorMat;
    wall.render!.batchGroupId = BATCH_ARENA;
    wall.setLocalScale(w.hw * 2, wallHeight, w.hd * 2);
    wall.setPosition(w.x, wallHeight / 2, w.z);
    app.root.addChild(wall);
  }

  // Collapse all static arena meshes into batched draw calls
  app.batcher.generate([BATCH_ARENA]);
}

// ── Lights ───────────────────────────────────────────────────
function buildLighting(app: pc.Application) {
  // Ambient
  app.scene.ambientLight = new pc.Color(0.32, 0.32, 0.38);

  // Fog — dark blue EXP2 fade hides the void edge smoothly
  app.scene.fog.type = pc.FOG_EXP2;
  app.scene.fog.color = new pc.Color(0.04, 0.04, 0.10);
  app.scene.fog.density = 0.012;

  // Directional sun
  const sun = new pc.Entity('sun');
  sun.addComponent('light', {
    type: 'directional',
    color: new pc.Color(1, 0.95, 0.85),
    intensity: 1.2,
    castShadows: true,
    shadowBias: 0.05,
    shadowDistance: 120,
  });
  sun.setLocalEulerAngles(50, 30, 0);
  app.root.addChild(sun);

  // Fill light (blue-ish from below)
  const fill = new pc.Entity('fill');
  fill.addComponent('light', {
    type: 'directional',
    color: new pc.Color(0.3, 0.4, 0.6),
    intensity: 0.3,
  });
  fill.setLocalEulerAngles(-40, 180, 0);
  app.root.addChild(fill);
}

// ── Main game bootstrap ─────────────────────────────────────
async function main() {
  setLoadProgress(10, 'Initializing engine...');

  const canvas = document.getElementById('application-canvas') as HTMLCanvasElement;

  const app = new pc.Application(canvas, {
    mouse: new pc.Mouse(canvas),
    keyboard: new pc.Keyboard(window),
    graphicsDeviceOptions: {
      antialias: true,
      alpha: false,
      preserveDrawingBuffer: false,
    },
  });

  app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
  app.setCanvasResolution(pc.RESOLUTION_AUTO);
  app.start();

  if (import.meta.env.DEV) {
    const { PcProfiler } = await import('@ironclash/pc-profiler');
    new PcProfiler(app);
  }

  window.addEventListener('resize', () => app.resizeCanvas());

  setLoadProgress(30, 'Building arena...');
  buildArena(app);
  buildSkyDome(app);
  buildLighting(app);

  setLoadProgress(50, 'Setting up systems...');

  const camera      = new CameraSystem(app);

  // Bloom post-processing — makes emissive objects (bullets, portals, power-ups) glow
  const bloom = new BloomEffect(app.graphicsDevice);
  bloom.bloomThreshold = 0.35;
  bloom.blurAmount = 4;
  bloom.bloomIntensity = 0.8;
  camera.entity.camera!.postEffects.addEffect(bloom);

  const input       = new InputSystem(app, canvas);
  const particles   = new ParticleSystem(app);
  const audio       = new AudioSystem();
  const debug       = new DebugSystem(app);
  const bullets     = new BulletPool(app);
  const hud         = new HUD();
  const leaderboard = new Leaderboard();

  // Mobile touch input — only instantiated on touch-capable devices
  const touchInput = TouchInput.detect() ? new TouchInput() : null;

  // Leaderboard button — always visible (top-centre)
  const lbBtn = document.getElementById('mobile-lb-btn');
  if (lbBtn) {
    lbBtn.style.display = 'block';
    lbBtn.addEventListener('click', () => leaderboard.toggle());
  }

  // Remote machines map: sessionId → MachineEntity
  const remoteMachines = new Map<string, MachineEntity>();
  // Interpolation targets and current rendered state for remote machines
  const remoteTargets  = new Map<string, { x: number; z: number; rotation: number; turretAngle: number }>();
  const remoteCurrent  = new Map<string, { x: number; z: number; rotation: number; turretAngle: number }>();
  // Sessions killed but not yet respawned — stateChange must not re-create their
  // entity while hp is still 0 (server keeps them in state.machines throughout).
  const deadRemotes = new Set<string>();
  // Name tag HTML elements for remote machines
  const nameTags = new Map<string, HTMLDivElement>();
  const nameTagLayer = document.getElementById('nametag-layer')!;

  // Destructibles map: id → DestructibleEntity
  const destructibles = new Map<string, DestructibleEntity>();

  // PowerUp boxes/orbs: id → PowerUpEntity
  const powerUps = new Map<string, PowerUpEntity>();

  // Portals: id → PortalEntity (permanent, spawned once at room creation)
  const portals = new Map<string, PortalEntity>();

  // (Bullets are now message-driven — no schema sync needed)
  let   pendingState: any = null;            // latest server state — processed once per game-loop frame

  // Local player state (predicted)
  let localMachine: MachineEntity | null = null;
  let localDead = false; // true while waiting for server respawn after death
  let deathScreenShownAt = 0; // performance.now() timestamp when death screen was shown
  const controller = new PlayerController(); // owns x, z, rotation, moveSpeed, turnSpeed, correction
  let localHp = 100;
  let localMaxHp = 100;
  let localCrystals = 0;
  let localXp = 0;
  let localLevel = 1;
  let localUpgrades = { weapon: 0, chassis: 0, movement: 0, attachment: null as string | null };
  let localActivePowerUp = '';   // synced — used for local bullet variant
  let localFireRateLevel = 0;    // synced — used in client fire timer
  let localBulletSpeedLevel = 0; // synced — used in client bullet spawn
  let localStatLevels: Record<string, number> = {};
  let localKills = 0;
  let localDeaths = 0;
  // Local countdown timers (schema only sends activation/expiry transitions)
  let localPowerUpType = '';
  let localPowerUpTimer = 0;
  let localFrozenTimer = 0;

  /** Clean up all client-side game entities (used when returning to waiting room). */
  function cleanupGameEntities() {
    // Remote machines
    remoteMachines.forEach(m => m.destroy());
    remoteMachines.clear();
    remoteTargets.clear();
    remoteCurrent.clear();
    deadRemotes.clear();
    // Name tags
    nameTags.forEach(tag => tag.remove());
    nameTags.clear();
    // Local machine
    localMachine?.destroy();
    localMachine = null;
    // Destructibles
    destructibles.forEach(d => d.destroy());
    destructibles.clear();
    // PowerUps
    powerUps.forEach(p => p.destroy());
    powerUps.clear();
    // Portals
    portals.forEach(p => p.destroy());
    portals.clear();
    // Flags
    flagEntities.forEach(f => f.destroy());
    flagEntities.clear();
    // Carrier beacons
    carrierBeacons.forEach(({ beacon, badge }) => { beacon.destroy(); badge?.remove(); });
    carrierBeacons.clear();
    // Base zone markers
    baseZones.forEach(z => z.destroy());
    baseZones.length = 0;
    localCarryingFlag = false;
    captureWaypoint.style.display = 'none';
    // Bullets
    bullets.destroyAll();
    // Reset local state
    localDead = false;
    localHp = 100;
    localMaxHp = 100;
    localCrystals = 0;
    localXp = 0;
    localLevel = 1;
    localUpgrades = { weapon: 0, chassis: 0, movement: 0, attachment: null };
    localActivePowerUp = '';
    localFireRateLevel = 0;
    localBulletSpeedLevel = 0;
    localStatLevels = {};
    localKills = 0;
    localDeaths = 0;
    localPowerUpType = '';
    localPowerUpTimer = 0;
    localFrozenTimer = 0;
    controller.resetTo(0, 0);
    pendingState = null;
    inWaitingRoom = true;
    // Stop audio
    audio.stopMusic();
    // Reset HUD
    hud.updateHealth(localHp, localMaxHp);
    hud.updateRewards(localCrystals, localXp, localLevel);
    hud.updateKills(localKills, localDeaths);
    ctfHud?.updateScore(0, 0);
    ctfHud?.updateTimer(0);
    hud.updateFFATimer(0);
  }

  setLoadProgress(70, 'Connecting to server...');

  // Global leaderboard (available on name screen before joining)
  const serverUrl = import.meta.env.VITE_SERVER_URL ?? 'ws://localhost:2567';
  const globalLeaderboard = new GlobalLeaderboard(serverUrl);
  document.getElementById('global-lb-btn')?.addEventListener('click', () => globalLeaderboard.toggle());

  // Help window (available everywhere)
  const helpWindow = new HelpWindow();

  // Show name screen
  loadingEl.classList.add('hide');
  nameScreen.classList.remove('hide');

  await new Promise<void>(resolve => {
    enterBtn.addEventListener('click', () => {
      if (nameInput.value.trim()) { audio.init(); resolve(); }
    });
    nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && nameInput.value.trim()) { audio.init(); resolve(); }
    });
  });

  const playerName = nameInput.value.trim() || 'Tank';
  nameScreen.classList.add('hide');

  // ── Mode selection ────────────────────────────────────────
  modeScreen.classList.remove('hide');
  const gameMode = await new Promise<'ffa' | 'ctf'>(resolve => {
    modeFfaBtn.addEventListener('click', () => resolve('ffa'), { once: true });
    modeCtfBtn.addEventListener('click', () => resolve('ctf'), { once: true });
  });
  modeScreen.classList.add('hide');

  // ── Lobby screen ────────────────────────────────────────
  lobbyTitle.textContent = gameMode === 'ctf' ? 'CAPTURE THE FLAG' : 'FREE FOR ALL';
  let selectedTeam: 'red' | 'blue' | null = null;
  let lobbyAction: 'create' | 'join' | null = null;

  function showTeamPicker(action: 'create' | 'join' | null) {
    lobbyAction = action;
    if (gameMode === 'ctf' && action) {
      lobbyTeamPicker.classList.remove('hide');
    } else {
      lobbyTeamPicker.classList.add('hide');
      selectedTeam = null;
      lobbyTeamRed.classList.remove('selected');
      lobbyTeamBlue.classList.remove('selected');
    }
  }

  lobbyTeamRed.addEventListener('click', () => {
    selectedTeam = 'red';
    lobbyTeamRed.classList.add('selected');
    lobbyTeamBlue.classList.remove('selected');
  });
  lobbyTeamBlue.addEventListener('click', () => {
    selectedTeam = 'blue';
    lobbyTeamBlue.classList.add('selected');
    lobbyTeamRed.classList.remove('selected');
  });

  lobbyCodeInput.addEventListener('focus', () => showTeamPicker('join'));
  lobbyBackBtn.addEventListener('click', () => window.location.reload());

  lobbyScreen.classList.remove('hide');

  type JoinParams =
    | { via: 'quickplay' }
    | { via: 'createPrivate'; team: 'red' | 'blue' | null }
    | { via: 'joinPrivate'; code: string; team: 'red' | 'blue' | null };

  const joinParams = await new Promise<JoinParams>(resolve => {
    lobbyQuickplayBtn.addEventListener('click', () => {
      resolve({ via: 'quickplay' });
    }, { once: true });

    lobbyCreateBtn.addEventListener('click', () => {
      resolve({ via: 'createPrivate', team: selectedTeam });
    });

    lobbyJoinBtn.addEventListener('click', () => {
      const code = lobbyCodeInput.value.trim();
      if (!code) { lobbyCodeInput.focus(); return; }
      resolve({ via: 'joinPrivate', code, team: selectedTeam });
    });

    lobbyCodeInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const code = lobbyCodeInput.value.trim();
        if (!code) return;
        resolve({ via: 'joinPrivate', code, team: selectedTeam });
      }
    });
  });

  lobbyScreen.classList.add('hide');
  loadingEl.classList.remove('hide');
  setLoadProgress(80, 'Connecting...');

  const network = new ColyseusClient(serverUrl);
  hud.setNetwork(network);

  // ── CTF state ─────────────────────────────────────────────
  let localTeam: 'red' | 'blue' | '' = '';
  let ctfHud: CTFHud | null = null;
  const flagEntities = new Map<string, FlagEntity>(); // 'red' | 'blue' → FlagEntity
  // carrierId → { beacon entity, team, name-tag badge element }
  const carrierBeacons = new Map<string, { beacon: pc.Entity; badge: HTMLSpanElement | null }>();
  let matchEnded = false;
  let inWaitingRoom = false;

  function createFlagBeacon(app: pc.Application, team: 'red' | 'blue'): pc.Entity {
    const root = new pc.Entity('flagBeacon');
    app.root.addChild(root);

    const [cr, cg, cb] = team === 'red' ? [0.9, 0.1, 0.1] : [0.1, 0.3, 0.9];

    function beaconMat(opacity: number): pc.StandardMaterial {
      const m = new pc.StandardMaterial();
      m.diffuse  = new pc.Color(cr, cg, cb);
      m.emissive = new pc.Color(cr, cg, cb);
      m.emissiveIntensity = 3.0;
      m.blendType  = pc.BLEND_NORMAL;
      m.depthWrite = false;
      m.opacity    = opacity;
      m.update();
      return m;
    }

    // Tall glowing cylinder — visible from far away
    const shaft = new pc.Entity('shaft');
    shaft.addComponent('render', { type: 'cylinder' });
    shaft.render!.castShadows = false;
    shaft.render!.receiveShadows = false;
    shaft.render!.meshInstances[0].material = beaconMat(0.7);
    shaft.setLocalScale(0.18, 4.0, 0.18);
    shaft.setLocalPosition(0, 5.0, 0);
    root.addChild(shaft);

    // Diamond tip
    const tip = new pc.Entity('tip');
    tip.addComponent('render', { type: 'cone' });
    tip.render!.castShadows = false;
    tip.render!.receiveShadows = false;
    tip.render!.meshInstances[0].material = beaconMat(1.0);
    tip.setLocalScale(0.5, 0.7, 0.5);
    tip.setLocalPosition(0, 7.4, 0);
    root.addChild(tip);

    return root;
  }

  // ── Base zone markers (permanent 3D rings at each team's home) ──────
  function createBaseZone(app: pc.Application, team: 'red' | 'blue', x: number, z: number): pc.Entity {
    const root = new pc.Entity(`baseZone_${team}`);
    app.root.addChild(root);
    root.setPosition(x, 0, z);

    const [cr, cg, cb] = team === 'red' ? [0.9, 0.1, 0.1] : [0.1, 0.3, 0.9];

    // Large glowing ring on the ground
    const ring = new pc.Entity('ring');
    ring.addComponent('render', { type: 'cylinder' });
    ring.render!.castShadows = false;
    ring.render!.receiveShadows = false;
    const ringMat = new pc.StandardMaterial();
    ringMat.diffuse  = new pc.Color(cr * 0.3, cg * 0.3, cb * 0.3);
    ringMat.emissive = new pc.Color(cr, cg, cb);
    ringMat.emissiveIntensity = 1.5;
    ringMat.blendType  = pc.BLEND_NORMAL;
    ringMat.depthWrite = false;
    ringMat.opacity    = 0.35;
    ringMat.update();
    ring.render!.meshInstances[0].material = ringMat;
    ring.setLocalScale(8, 0.05, 8);
    ring.setLocalPosition(0, 0.02, 0);
    root.addChild(ring);

    // Vertical pillar of light so it's visible from distance
    const pillar = new pc.Entity('pillar');
    pillar.addComponent('render', { type: 'cylinder' });
    pillar.render!.castShadows = false;
    pillar.render!.receiveShadows = false;
    const pillarMat = new pc.StandardMaterial();
    pillarMat.diffuse  = new pc.Color(cr, cg, cb);
    pillarMat.emissive = new pc.Color(cr, cg, cb);
    pillarMat.emissiveIntensity = 2.0;
    pillarMat.blendType  = pc.BLEND_NORMAL;
    pillarMat.depthWrite = false;
    pillarMat.opacity    = 0.15;
    pillarMat.update();
    pillar.render!.meshInstances[0].material = pillarMat;
    pillar.setLocalScale(0.4, 12, 0.4);
    pillar.setLocalPosition(0, 6, 0);
    root.addChild(pillar);

    return root;
  }
  const baseZones: pc.Entity[] = [];
  const _cwPos = new pc.Vec3(); // reusable scratch vec for capture waypoint

  // ── Capture waypoint (screen-space arrow when carrying flag) ────────
  let localCarryingFlag = false;
  const captureWaypoint = document.createElement('div');
  captureWaypoint.id = 'capture-waypoint';
  captureWaypoint.style.cssText = [
    'position:fixed',
    'z-index:20',
    'pointer-events:none',
    'display:none',
    'flex-direction:column',
    'align-items:center',
    'font-family:inherit',
    'transition:opacity 0.2s',
  ].join(';');
  captureWaypoint.innerHTML = `
    <div id="cw-arrow" style="font-size:28px;line-height:1;text-shadow:0 0 12px currentColor;">▼</div>
    <div id="cw-label" style="font-size:11px;font-weight:800;letter-spacing:0.15em;text-transform:uppercase;white-space:nowrap;text-shadow:0 0 8px currentColor;margin-top:2px;">CAPTURE HERE</div>
  `;
  document.body.appendChild(captureWaypoint);

  // Applies team-colored border/text to a name tag element (CTF only).
  // In FFA mode (no team) this is a no-op.
  function applyTeamTagStyle(tag: HTMLDivElement, team: string | undefined, isBot: boolean) {
    if (!team || gameMode !== 'ctf') return;
    const isRed = team === 'red';
    tag.style.borderColor = isRed ? 'rgba(232,64,64,0.7)' : 'rgba(64,128,232,0.7)';
    tag.style.color       = isRed ? '#ff9090' : '#90b8ff';
    // Dim allies slightly vs enemies to help with quick identification
    const isFriendly = team === localTeam;
    tag.style.opacity = isFriendly ? '0.8' : '1';
  }

  // ── Network state sync ─────────────────────────────────
  // Coalesced: when the server delivers a burst of ticks (network hiccup /
  // GC pause on the server), stateChange fires multiple times before the
  // next rAF. We store the latest state and process it once per game-loop
  // frame to avoid redundant entity rebuilds, corrections, and Set allocs.
  function processNetworkState(state: any) {
    // Sync remote machines
    state.machines.forEach((machineState: any, sessionId: string) => {
      if (sessionId === network.sessionId) {
        // Respawn detection: server never removes/re-adds the machine — it just
        // mutates hp and position. Detect the hp 0→positive transition instead.
        if (localDead && machineState.hp > 0 && performance.now() - deathScreenShownAt > 1500) {
          localDead = false;
          hud.hideDeathScreen();
          localMachine?.destroy();
          localMachine = new MachineEntity(app, true, playerName, machineState.colorIndex);
          controller.resetTo(machineState.x, machineState.z);
          localMachine.setPosition(controller.x, controller.z);
        }

        // Re-create local machine if it was cleaned up (e.g. between private room rounds)
        if (!localMachine && !localDead && machineState.hp > 0) {
          localMachine = new MachineEntity(app, true, playerName, machineState.colorIndex);
          controller.resetTo(machineState.x, machineState.z);
          localMachine.setPosition(controller.x, controller.z);
        }

        // Server reconciliation.
        // Queue server correction; the game loop drains it smoothly each frame.
        controller.applyServerCorrection(machineState.x, machineState.z);

        const prevHp = localHp;
        localHp  = machineState.hp;
        localMaxHp = machineState.maxHp;
        // Visual + audio hit feedback (ignore respawn HP jumps when prevHp=0)
        if (localHp < prevHp && prevHp > 0) {
          const damage = prevHp - localHp;
          audio.hit();
          hud.showHitFlash(damage, localMaxHp);
          camera.screenShake(Math.min(0.7, (damage / localMaxHp) * 5), 0.18);
        }
        localCrystals = machineState.crystals;
        localXp  = machineState.xp;
        localLevel = machineState.level;
        controller.moveSpeed  = machineState.moveSpeed  ?? 6;
        controller.turnSpeed  = machineState.turnSpeed  ?? 2.5;
        localActivePowerUp    = machineState.activePowerUp   ?? '';
        localFireRateLevel    = machineState.fireRateLevel    ?? 0;
        localBulletSpeedLevel = machineState.bulletSpeedLevel ?? 0;
        localKills  = machineState.kills  ?? 0;
        localDeaths = machineState.deaths ?? 0;

        localUpgrades = {
          weapon:     machineState.weaponTier ?? 0,
          chassis:    machineState.chassisTier ?? 0,
          movement:   machineState.movementTier ?? 0,
          attachment: machineState.attachment ?? null,
        };

        localStatLevels = {
          speedLevel:       machineState.speedLevel       ?? 0,
          bulletSpeedLevel: machineState.bulletSpeedLevel ?? 0,
          regenLevel:       machineState.regenLevel       ?? 0,
          bulletDmgLevel:   machineState.bulletDmgLevel   ?? 0,
          maxHealthLevel:   machineState.maxHealthLevel   ?? 0,
          fireRateLevel:    machineState.fireRateLevel    ?? 0,
          armorLevel:       machineState.armorLevel       ?? 0,
          bodyDmgLevel:     machineState.bodyDmgLevel     ?? 0,
          turnSpeedLevel:   machineState.turnSpeedLevel   ?? 0,
        };

        if (localMachine) {
          // Do NOT set position here — the game loop owns visual position
          localMachine.buildWeapon(localUpgrades.weapon);
          localMachine.buildChassis(localUpgrades.chassis);
          localMachine.buildMovement(localUpgrades.movement);
          localMachine.updateHpBar(localHp, localMaxHp);
          // Sync frozen timer from schema transitions (server sends 7 on activation, 0 on expiry)
          const serverFrozen = machineState.frozenTimer ?? 0;
          if (serverFrozen > 0 && localFrozenTimer <= 0) localFrozenTimer = serverFrozen;
          else if (serverFrozen <= 0) localFrozenTimer = 0;
          localMachine.setFrozen(localFrozenTimer > 0);

          // Sync powerup timer from schema transitions (server sends duration on activation, 0 on expiry)
          const serverPuType = machineState.activePowerUp ?? '';
          const serverPuTimer = machineState.powerUpTimer ?? 0;
          if (serverPuType && serverPuTimer > 0 && (serverPuType !== localPowerUpType || localPowerUpTimer <= 0)) {
            localPowerUpType = serverPuType;
            localPowerUpTimer = serverPuTimer;
          } else if (!serverPuType || serverPuTimer <= 0) {
            localPowerUpType = '';
            localPowerUpTimer = 0;
          }
        }

        hud.updateHealth(localHp, localMaxHp);
        hud.updateRewards(localCrystals, localXp, localLevel);
        hud.updateKills(localKills, localDeaths);
        hud.setOwnedUpgrades(localUpgrades);
        hud.setStatLevels(localStatLevels);
        hud.updatePowerUpTimer(localPowerUpType, localPowerUpTimer);
        return;
      }

      // Remote machine — skip while dead (waiting for server respawn).
      // machineKilled already destroyed the entity; without this guard stateChange
      // would immediately re-create it because the server keeps the machine in
      // state.machines with hp=0 for the full respawn duration.
      if (deadRemotes.has(sessionId)) {
        if (machineState.hp > 0) {
          // Respawned — remove from dead set and fall through to create entity
          deadRemotes.delete(sessionId);
        } else {
          return; // still dead, nothing to render
        }
      }

      // Race-condition guard: stateChange can deliver hp=0 before machineKilled fires.
      // If so, destroy the entity immediately so it never lingers visually.
      // machineKilled will still fire its kill-feed entry and audio shortly after.
      if (machineState.hp <= 0) {
        const dying = remoteMachines.get(sessionId);
        if (dying) {
          const pos = dying.root.getPosition();
          particles.tankDestroyed(pos.x, pos.y, pos.z, false);
          dying.destroy();
          remoteMachines.delete(sessionId);
          remoteTargets.delete(sessionId);
          remoteCurrent.delete(sessionId);
          nameTags.get(sessionId)?.remove();
          nameTags.delete(sessionId);
        }
        deadRemotes.add(sessionId);
        return;
      }

      let remote = remoteMachines.get(sessionId);
      if (!remote) {
        remote = new MachineEntity(app, false, machineState.name ?? sessionId, machineState.colorIndex);
        remoteMachines.set(sessionId, remote);
        // Seed current state so first frame has no pop
        const init = { x: machineState.x, z: machineState.z, rotation: machineState.rotation, turretAngle: machineState.turretAngle };
        remoteCurrent.set(sessionId, { ...init });
        remote.setPosition(init.x, init.z);
        remote.setBodyRotation(init.rotation);
        remote.setTurretAngle(init.turretAngle);

        // Also create a name tag for the respawned machine
        if (!nameTags.has(sessionId)) {
          const tag = document.createElement('div');
          tag.className = machineState.isBot ? 'nametag bot-tag' : 'nametag';
          tag.textContent = machineState.name ?? sessionId;
          applyTeamTagStyle(tag, machineState.team, machineState.isBot);
          nameTagLayer.appendChild(tag);
          nameTags.set(sessionId, tag);
        }
      }
      // Store target for smooth interpolation in game loop
      remoteTargets.set(sessionId, {
        x: machineState.x,
        z: machineState.z,
        rotation: machineState.rotation,
        turretAngle: machineState.turretAngle,
      });
      remote.updateHpBar(machineState.hp, machineState.maxHp);
      remote.buildWeapon(machineState.weaponTier ?? 0);
      remote.buildChassis(machineState.chassisTier ?? 0);
      remote.buildMovement(machineState.movementTier ?? 0);
      remote.setFrozen((machineState.frozenTimer ?? 0) > 0);
      // Hide entity while star invisibility is active
      remote.root.enabled = (machineState.activePowerUp !== 'star');
    });

    // Sync destructibles
    state.destructibles.forEach((d: any, id: string) => {
      if (!destructibles.has(id)) {
        const def = DESTRUCTIBLE_DEFS[d.type as DestructibleType];
        if (def) {
          const ent = new DestructibleEntity(app, id, d.type as DestructibleType, d.x, d.z, d.maxHp ?? def.hp, d.rotation ?? 0);
          destructibles.set(id, ent);
        }
      } else {
        destructibles.get(id)?.updateHp(d.hp);
      }
    });
    // Remove client-side entities for destructibles the server has already deleted.
    // The server deletes entries from state.destructibles immediately on destruction;
    // objectDestroyed handles the normal path but may fire before the entity is
    // created, leaving a stale entity if the state sync runs first.
    destructibles.forEach((ent, id) => {
      if (!state.destructibles.has(id)) {
        ent.destroy();
        destructibles.delete(id);
      }
    });

    // Sync PowerUp boxes/orbs
    state.powerUps.forEach((pu: any, id: string) => {
      if (!pu.alive) {
        powerUps.get(id)?.destroy();
        powerUps.delete(id);
        return;
      }
      let ent = powerUps.get(id);
      if (!ent) {
        ent = new PowerUpEntity(app, id, pu.type as PowerUpType, pu.x, pu.z);
        powerUps.set(id, ent);
      }
      ent.setPhase(pu.phase);
      ent.updateHp(pu.hp, pu.maxHp);
    });

    // Sync portals (permanent — only spawn, never removed)
    state.portals?.forEach((p: any, id: string) => {
      if (!portals.has(id)) {
        portals.set(id, new PortalEntity(app, id, p.x, p.z, p.colorIndex ?? 0));
      }
    });

    // Sync CTF flags (only present in CTF mode)
    const _ctfHud = ctfHud;
    if (state.flags && _ctfHud) {
      // Collect current carriers so we can diff against previous frame
      const newCarriers = new Map<string, 'red' | 'blue'>(); // carrierId → team

      // Spawn base zone markers once
      if (baseZones.length === 0) {
        baseZones.push(createBaseZone(app, 'red', CTF_RED_FLAG_HOME.x, CTF_RED_FLAG_HOME.z));
        baseZones.push(createBaseZone(app, 'blue', CTF_BLUE_FLAG_HOME.x, CTF_BLUE_FLAG_HOME.z));
      }

      state.flags.forEach((flagState: any, team: string) => {
        const t = team as 'red' | 'blue';
        let flagEnt = flagEntities.get(t);
        if (!flagEnt) {
          const home = t === 'red' ? CTF_RED_FLAG_HOME : CTF_BLUE_FLAG_HOME;
          flagEnt = new FlagEntity(app, t, home.x, home.z);
          flagEntities.set(t, flagEnt);
        }
        if (flagState.atBase) {
          flagEnt.setAtBase();
          _ctfHud.updateFlagStatus(t, 'atBase');
        } else if (flagState.carrierId) {
          flagEnt.setCarried();
          _ctfHud.updateFlagStatus(t, 'carried');
          newCarriers.set(flagState.carrierId as string, t);
        } else {
          flagEnt.setDropped(flagState.x, flagState.z);
          _ctfHud.updateFlagStatus(t, 'dropped');
        }
      });

      // Remove beacons for players who no longer carry a flag
      carrierBeacons.forEach(({ beacon, badge }, id) => {
        if (!newCarriers.has(id)) {
          beacon.destroy();
          badge?.remove();
          carrierBeacons.delete(id);
        }
      });

      // Add beacons for new carriers
      newCarriers.forEach((team, carrierId) => {
        if (!carrierBeacons.has(carrierId)) {
          const beacon = createFlagBeacon(app, team);
          // Name-tag badge
          const tag = nameTags.get(carrierId);
          let badge: HTMLSpanElement | null = null;
          if (tag) {
            badge = document.createElement('span');
            badge.className = 'flag-badge';
            badge.textContent = ' 🚩';
            badge.style.cssText = `color:${team === 'red' ? '#ff6060' : '#6090ff'}; font-size:14px;`;
            tag.appendChild(badge);
          }
          // Local player badge — show on HUD instead of a name tag
          if (carrierId === network.sessionId) {
            _ctfHud.showNotification(`You have the ${team.toUpperCase()} flag! Capture it!`,
              team === 'red' ? '#ff8080' : '#80b0ff', 5000);
          }
          carrierBeacons.set(carrierId, { beacon, badge });
        }
      });

      // Track whether local player is carrying a flag
      localCarryingFlag = newCarriers.has(network.sessionId);

      _ctfHud.updateScore(state.redScore ?? 0, state.blueScore ?? 0);
      _ctfHud.updateTimer(state.matchTimeLeft ?? 0);
    }

    // FFA timer sync
    if (!ctfHud && state.matchTimeLeft != null) {
      hud.updateFFATimer(state.matchTimeLeft);
    }

    // Sync local team from own machine state (update on every change, not just first)
    const ownMachine = state.machines.get(network.sessionId);
    if (ownMachine && ownMachine.team && ownMachine.team !== localTeam) {
      localTeam = ownMachine.team as 'red' | 'blue';
      ctfHud?.setLocalTeam(localTeam);
    }

    leaderboard.update(state.machines, network.sessionId);
  }

  // Just record the latest state — processNetworkState runs once per game-loop frame
  network.on('stateChange', (state) => { pendingState = state; });

  network.on('machineAdded', (sessionId, machine) => {
    if (sessionId === network.sessionId) {
      hud.hideDeathScreen();
      localMachine?.destroy(); // remove any stale entity from before respawn
      localMachine = new MachineEntity(app, true, playerName, machine.colorIndex);
      controller.x = machine.x ?? 0;
      controller.z = machine.z ?? 0;
      localMachine.setPosition(controller.x, controller.z);
    } else {
      const name = machine.name ?? sessionId;
      const remote = new MachineEntity(app, false, name, machine.colorIndex);
      remote.setPosition(machine.x, machine.z);
      remoteMachines.set(sessionId, remote);
      const init = { x: machine.x, z: machine.z, rotation: machine.rotation ?? 0, turretAngle: machine.turretAngle ?? 0 };
      remoteCurrent.set(sessionId, { ...init });
      remoteTargets.set(sessionId, { ...init });

      const tag = document.createElement('div');
      tag.className = machine.isBot ? 'nametag bot-tag' : 'nametag';
      tag.textContent = name;
      applyTeamTagStyle(tag, machine.team, machine.isBot);
      nameTagLayer.appendChild(tag);
      nameTags.set(sessionId, tag);
    }
  });

  network.on('machineRemoved', (sessionId) => {
    remoteMachines.get(sessionId)?.destroy();
    remoteMachines.delete(sessionId);
    remoteTargets.delete(sessionId);
    remoteCurrent.delete(sessionId);
    deadRemotes.delete(sessionId);
    nameTags.get(sessionId)?.remove();
    nameTags.delete(sessionId);
    const cb = carrierBeacons.get(sessionId);
    if (cb) { cb.beacon.destroy(); cb.badge?.remove(); carrierBeacons.delete(sessionId); }
  });

  // ── Bullet messages (replaces schema sync) ────────────────
  network.on('bulletSpawn', (data) => {
    if (matchEnded || inWaitingRoom) return;
    if (data.ownerId === network.sessionId) return; // local bullets already predicted
    bullets.spawnServer(data.id, data.x, data.z, data.vx, data.vz, data.weaponTier, data.ttl, data.bulletVariant ?? 0);
  });

  network.on('bulletDespawn', (data) => {
    if (matchEnded || inWaitingRoom) return;
    bullets.remove(data.id);
  });

  network.on('objectDestroyed', (data) => {
    if (matchEnded || inWaitingRoom) return;
    const ent = destructibles.get(data.id);
    if (ent) {
      const pos = ent.entity.getPosition();

      if (data.type === 'crystal_shard') {
        particles.crystalBurst(pos.x, pos.y, pos.z);
      } else if (data.type === 'barrel') {
        particles.explosion(pos.x, pos.y, pos.z, 1.8); // barrels explode bigger
        camera.screenShake(0.6, 0.3);
        audio.explosion(1.8);
      } else {
        particles.explosion(pos.x, pos.y, pos.z, 1.0);
        audio.explosion(1.0);
      }

      ent.destroy();
      destructibles.delete(data.id);
    }
  });

  network.on('machineKilled', (data) => {
    if (matchEnded || inWaitingRoom) return;
    hud.addKillFeedEntry(data.killerName, data.victimName);
    if (data.killerId === network.sessionId) {
      hud.showKillNotification(data.victimName);
    }

    const isLocalDeath = data.victimId === network.sessionId;

    if (isLocalDeath) {
      // Capture position before destroying the entity
      const pos = localMachine?.root.getPosition();
      const px = pos?.x ?? controller.x;
      const py = pos?.y ?? 0;
      const pz = pos?.z ?? controller.z;

      particles.tankDestroyed(px, py, pz, true);
      audio.machineDestroyed(true);
      camera.screenShake(1.8, 0.8);

      localMachine?.destroy();
      localMachine = null;
      localDead = true;
      deathScreenShownAt = performance.now();
      hud.showDeathScreen();
    } else {
      const remote = remoteMachines.get(data.victimId);
      if (remote) {
        // Normal path: entity still exists (machineKilled fires before stateChange processes hp=0)
        const pos = remote.root.getPosition();
        particles.tankDestroyed(pos.x, pos.y, pos.z, false);
        audio.machineDestroyed(false);
        camera.screenShake(0.45, 0.25);
        remote.destroy();
      } else {
        // processNetworkState already cleaned up the entity (stateChange raced ahead);
        // particles already fired there — still play audio + screenshake
        audio.machineDestroyed(false);
        camera.screenShake(0.45, 0.25);
      }
      // Mark as dead so stateChange doesn't re-create the entity during respawn window
      deadRemotes.add(data.victimId);
      remoteMachines.delete(data.victimId);
      remoteTargets.delete(data.victimId);
      remoteCurrent.delete(data.victimId);
      nameTags.get(data.victimId)?.remove();
      nameTags.delete(data.victimId);
    }
  });

  network.on('rewardGain', (data) => {
    if (matchEnded || inWaitingRoom) return;
    localCrystals += data.crystals;
    localXp += data.xp;
    hud.updateRewards(localCrystals, localXp, localLevel);
    audio.crystalPickup();

    if (data.levelUp && data.newLevel) {
      localLevel = data.newLevel;
      hud.showLevelUp(data.newLevel);
      audio.levelUp();
    }

    // Show popup near center-bottom
    hud.showRewardPopup(data.crystals, data.xp, window.innerWidth / 2 + (Math.random() - 0.5) * 200, window.innerHeight * 0.6);
  });

  network.on('upgradeApplied', (data) => {
    if (matchEnded || inWaitingRoom) return;
    const isLocal = data.sessionId === network.sessionId;
    const target = isLocal ? localMachine : remoteMachines.get(data.sessionId);
    if (!target) return;

    if (data.slot === 'weapon')   target.buildWeapon(data.tierId as number);
    if (data.slot === 'chassis')  target.buildChassis(data.tierId as number);
    if (data.slot === 'movement') target.buildMovement(data.tierId as number);
  });

  network.on('powerUpBoxBroken', (data: { id: string; type: string; x: number; z: number }) => {
    if (matchEnded || inWaitingRoom) return;
    particles.explosion(data.x, 1.5, data.z, 1.2);
    audio.explosion(0.8);
  });

  network.on('powerUpCollected', (data: { type: string; collectorId: string; x: number; z: number }) => {
    if (matchEnded || inWaitingRoom) return;
    particles.crystalBurst(data.x, 1.5, data.z);
    audio.crystalPickup();
  });

  network.on('powerUpActivated', (data: { type: string }) => {
    hud.showPowerUpBanner(data.type);
  });

  network.on('powerUpExpired', () => {
    hud.clearPowerUpBanner();
  });

  network.on('playerTeleported', (data) => {
    if (matchEnded || inWaitingRoom) return;
    // Entry burst at departure point, exit burst at arrival point
    particles.crystalBurst(data.fromX, 0, data.fromZ);
    particles.crystalBurst(data.toX, 0, data.toZ);
    if (data.sessionId === network.sessionId) {
      audio.crystalPickup(); // closest available audio cue for teleport
    }
  });

  network.on('error', (msg) => console.error('Network error:', msg));

  // ── CTF network events ─────────────────────────────────
  network.on('flagPickedUp', (data) => {
    if (!ctfHud || matchEnded || inWaitingRoom) return;
    const isOwn = data.carrierId === network.sessionId;
    const teamLabel = data.team.toUpperCase();
    const msg = isOwn
      ? `You picked up the ${teamLabel} flag!`
      : `${data.carrierName} picked up the ${teamLabel} flag!`;
    const color = data.team === 'red' ? '#ff8080' : '#80b0ff';
    ctfHud.showNotification(msg, color);
    ctfHud.updateFlagStatus(data.team as 'red' | 'blue', 'carried');
    flagEntities.get(data.team as 'red' | 'blue')?.setCarried();
  });

  network.on('flagDropped', (data) => {
    if (!ctfHud || matchEnded || inWaitingRoom) return;
    ctfHud.showNotification(`${data.team.toUpperCase()} flag dropped!`, '#cccccc', 2000);
    ctfHud.updateFlagStatus(data.team as 'red' | 'blue', 'dropped');
    flagEntities.get(data.team as 'red' | 'blue')?.setDropped(data.x, data.z);
  });

  network.on('flagCaptured', (data) => {
    if (!ctfHud || matchEnded || inWaitingRoom) return;
    const teamLabel = data.scoringTeam.toUpperCase();
    const color = data.scoringTeam === 'red' ? '#ff8080' : '#80b0ff';
    ctfHud.showNotification(`${teamLabel} CAPTURES! ${data.carrierName} scored!`, color, 4000);
    ctfHud.updateScore(data.redScore, data.blueScore);
    const enemyTeam = data.scoringTeam === 'red' ? 'blue' : 'red';
    const home = enemyTeam === 'red' ? CTF_RED_FLAG_HOME : CTF_BLUE_FLAG_HOME;
    flagEntities.get(enemyTeam)?.setReturned(home.x, home.z);
    ctfHud.updateFlagStatus(enemyTeam, 'atBase');
    audio.levelUp(); // repurpose level-up fanfare for capture notification
  });

  network.on('flagReturned', (data) => {
    if (!ctfHud || matchEnded || inWaitingRoom) return;
    ctfHud.showNotification(`${data.team.toUpperCase()} flag returned!`, '#aaffaa', 2500);
    const home = data.team === 'red' ? CTF_RED_FLAG_HOME : CTF_BLUE_FLAG_HOME;
    flagEntities.get(data.team as 'red' | 'blue')?.setReturned(home.x, home.z);
    ctfHud.updateFlagStatus(data.team as 'red' | 'blue', 'atBase');
  });

  // matchEnded is handled differently for private vs public — see below

  const isPrivate = joinParams.via === 'createPrivate' || joinParams.via === 'joinPrivate';

  try {
    if (joinParams.via === 'quickplay') {
      if (gameMode === 'ctf') {
        await network.joinCTF(playerName);
      } else {
        await network.joinArena(playerName);
      }
    } else if (joinParams.via === 'createPrivate') {
      const roomType = gameMode === 'ctf' ? 'ctf' : 'arena';
      await network.createPrivate(
        roomType, playerName, joinParams.team ?? undefined,
      );
    } else {
      await network.joinPrivate(
        joinParams.code, playerName, joinParams.team ?? undefined,
      );
    }

    if (gameMode === 'ctf') {
      ctfHud = new CTFHud();
      ctfHud.setRoomId(network.roomId);
    } else {
      hud.showFFATimer();
    }
  } catch (e) {
    const msg = joinParams.via === 'joinPrivate'
      ? 'Room not found. Check your code and try again.'
      : 'Failed to connect. Is the server running?';
    setLoadProgress(100, msg);
    return;
  }

  // ── Waiting room for private rooms ────────────────────────
  if (isPrivate) {
    loadingEl.classList.add('hide');
    const roomId = network.roomId;

    waitingRoomCode.textContent = roomId;
    waitingRoomTitle.textContent = gameMode === 'ctf'
      ? 'CAPTURE THE FLAG'
      : 'FREE FOR ALL';

    // Copy button
    waitingRoomCopy.addEventListener('click', () => {
      navigator.clipboard.writeText(roomId).then(() => {
        waitingRoomCopy.textContent = 'Copied!';
        setTimeout(() => { waitingRoomCopy.textContent = 'Copy Code'; }, 2000);
      });
    });

    // Leave room button
    waitingRoomBack.addEventListener('click', () => {
      network.leave();
      window.location.reload();
    });

    // Team switch buttons (CTF only)
    if (gameMode === 'ctf') {
      waitingRoomTeams.classList.remove('hide');
      wrTeamRed.addEventListener('click', () => {
        network.switchTeam('red');
      });
      wrTeamBlue.addEventListener('click', () => {
        network.switchTeam('blue');
      });
    }

    // Roster update function — reads machines from room state
    const updateRoster = () => {
      const state = network.state;
      if (!state?.machines) return;
      waitingRoomPlayers.innerHTML = '';
      const hostId = state.hostId ?? '';

      // Sync own team highlight from server state
      if (gameMode === 'ctf') {
        const own = state.machines.get(network.sessionId);
        if (own?.team) {
          wrTeamRed.classList.toggle('selected', own.team === 'red');
          wrTeamBlue.classList.toggle('selected', own.team === 'blue');
        }
      }

      state.machines.forEach((machine: any, sessionId: string) => {
        const row = document.createElement('div');
        row.className = 'wr-player';
        const dot = document.createElement('div');
        dot.className = `wr-player-dot ${machine.team || 'none'}`;
        const name = document.createElement('div');
        name.className = 'wr-player-name';
        name.textContent = machine.name || 'Player';
        row.appendChild(dot);
        row.appendChild(name);
        if (sessionId === hostId) {
          const badge = document.createElement('span');
          badge.className = 'wr-player-host';
          badge.textContent = 'HOST';
          row.appendChild(badge);
        }
        waitingRoomPlayers.appendChild(row);
      });

      // Show/hide start button + duration picker based on host status
      const isHost = network.sessionId === hostId;
      waitingRoomStart.style.display = isHost ? '' : 'none';
      waitingRoomDuration.style.display = isHost ? '' : 'none';
      waitingRoomStatus.style.display = isHost ? 'none' : '';
    };

    // Start match (host only)
    waitingRoomStart.addEventListener('click', () => {
      const minutes = Math.max(0, parseFloat(wrDurationInput.value) || 0);
      network.startMatch(Math.round(minutes * 60));
      waitingRoomStart.disabled = true;
      waitingRoomStart.textContent = 'Starting...';
    });

    // Listen for state changes to update roster
    network.on('stateChange', updateRoster);
    network.on('machineAdded', updateRoster);
    network.on('machineRemoved', updateRoster);

    // Match ended → show results overlay → clean up → return to waiting room
    network.on('matchEnded', async (data) => {
      matchEnded = true;
      // Stop game music and play result jingle
      audio.stopMusic();
      if (data.winner === 'draw') {
        audio.draw();
      } else if (gameMode === 'ctf' ? data.winner === localTeam : data.winner === playerName) {
        audio.victory();
      } else {
        audio.defeat();
      }
      if (ctfHud) {
        await ctfHud.showMatchEnd(data.winner, data.redScore, data.blueScore, 'waiting room');
      } else {
        await hud.showFFAMatchEnd(data.winner, data.topKills ?? 0, 'waiting room');
      }
      // Tear down all game entities from the finished round
      cleanupGameEntities();
      // Reset client state for the next round
      matchEnded = false;
      waitingRoomStart.disabled = false;
      waitingRoomStart.textContent = 'Start Match';
      waitingRoom.classList.remove('hide');
      updateRoster();
    });

    // Wait for the first matchStarted
    const showWaitingRoom = () => {
      waitingRoom.classList.remove('hide');
      updateRoster();
    };
    showWaitingRoom();

    await new Promise<void>(resolve => {
      network.on('matchStarted', () => {
        inWaitingRoom = false;
        matchEnded = false;
        waitingRoom.classList.add('hide');
        audio.startMusic();
        resolve();
      });
    });

    // For subsequent rounds, matchStarted hides the waiting room directly
    network.on('matchStarted', () => {
      inWaitingRoom = false;
      matchEnded = false;
      waitingRoom.classList.add('hide');
      audio.startMusic();
    });
  } else {
    // Public rooms: show result overlay then reload
    network.on('matchEnded', (data) => {
      matchEnded = true;
      audio.stopMusic();
      if (data.winner === 'draw') {
        audio.draw();
      } else if (gameMode === 'ctf' ? data.winner === localTeam : data.winner === playerName) {
        audio.victory();
      } else {
        audio.defeat();
      }
      if (ctfHud) {
        ctfHud.showMatchEnd(data.winner, data.redScore, data.blueScore);
      } else {
        hud.showFFAMatchEnd(data.winner, data.topKills ?? 0);
      }
      setTimeout(() => window.location.reload(), 6_000);
    });
  }

  audio.startMusic();
  setLoadProgress(100, 'Entering arena...');
  setTimeout(() => loadingEl.classList.add('hide'), 600);

  // ── Dev shortcuts ────────────────────────────────────────
  if (import.meta.env.DEV) {
    window.addEventListener('keydown', (e) => {
      // $ → add 1000 crystals
      if (e.key === '$') network.devAddCrystals(1000);
    });
  }

  // ── Auto-fire toggle ────────────────────────────────────
  let autoFire = false;

  const autoFireBtn = document.createElement('div');
  autoFireBtn.id = 'auto-fire-btn';
  autoFireBtn.innerHTML = '<span class="btn-icon">⚡</span> AUTO <span class="key-hint">[F]</span>';
  autoFireBtn.style.cssText = [
    'position:fixed',
    'bottom:80px',
    'right:24px',
    'z-index:12',
    'padding:9px 16px',
    'border-radius:8px',
    'font-size:11px',
    'font-weight:800',
    'letter-spacing:0.1em',
    'text-transform:uppercase',
    'color:rgba(255,255,255,0.65)',
    'background:rgba(6,6,18,0.88)',
    'border:1.5px solid rgba(255,255,255,0.35)',
    'cursor:pointer',
    'user-select:none',
    'pointer-events:all',
    'backdrop-filter:blur(14px)',
    'box-shadow:0 2px 12px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.06)',
    'transition:all 0.15s',
  ].join(';');

  function setAutoFire(on: boolean) {
    autoFire = on;
    if (on) {
      autoFireBtn.style.color = '#ff6060';
      autoFireBtn.style.borderColor = 'rgba(255,60,60,0.6)';
      autoFireBtn.style.background = 'rgba(140,0,0,0.28)';
      autoFireBtn.style.boxShadow = '0 0 18px rgba(255,50,50,0.4),inset 0 1px 0 rgba(255,255,255,0.06)';
    } else {
      autoFireBtn.style.color = 'rgba(255,255,255,0.65)';
      autoFireBtn.style.borderColor = 'rgba(255,255,255,0.35)';
      autoFireBtn.style.background = 'rgba(6,6,18,0.88)';
      autoFireBtn.style.boxShadow = '0 2px 12px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.06)';
    }
  }

  // On touch devices reposition the button into the gap between
  // the fire button (bottom: 58px, 82px tall → top at 140px) and
  // the right joystick (bottom: 192px), centred in that 52px slot.
  autoFireBtn.addEventListener('click', () => setAutoFire(!autoFire));

  // Move auto-fire into the action bar so all 3 buttons sit inline together
  const actionBar = document.getElementById('action-bar');
  if (actionBar) {
    autoFireBtn.style.position = 'relative';
    autoFireBtn.style.bottom = 'auto';
    autoFireBtn.style.right = 'auto';
    autoFireBtn.style.zIndex = '';
    actionBar.prepend(autoFireBtn);
  } else {
    document.body.appendChild(autoFireBtn);
  }

  // ── Mute toggle ─────────────────────────────────────────
  const muteBtn = document.createElement('div');
  muteBtn.id = 'mute-btn';
  muteBtn.textContent = '♪';
  muteBtn.style.cssText = [
    'position:fixed',
    'top:10px',
    'right:10px',
    'z-index:12',
    'width:32px',
    'height:32px',
    'border-radius:50%',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'font-size:15px',
    'color:rgba(255,255,255,0.45)',
    'background:rgba(0,0,0,0.45)',
    'border:2px solid rgba(255,255,255,0.12)',
    'cursor:pointer',
    'user-select:none',
    'backdrop-filter:blur(6px)',
    'transition:color 0.15s,border-color 0.15s',
  ].join(';');

  function syncMuteBtn() {
    const isMuted = audio.muted;
    muteBtn.textContent = isMuted ? '🔇' : '♪';
    muteBtn.style.color = isMuted ? 'rgba(255,80,80,0.7)' : 'rgba(255,255,255,0.45)';
    muteBtn.style.borderColor = isMuted ? 'rgba(255,80,80,0.4)' : 'rgba(255,255,255,0.12)';
  }
  syncMuteBtn();
  muteBtn.addEventListener('click', () => {
    audio.toggleMute();
    syncMuteBtn();
  });
  document.body.appendChild(muteBtn);

  // ── Global leaderboard toggle (in-game) ────────────────
  const globalLbBtn = document.createElement('div');
  globalLbBtn.id = 'global-lb-ingame-btn';
  globalLbBtn.textContent = '\uD83C\uDFC6';
  globalLbBtn.style.cssText = [
    'position:fixed',
    'top:10px',
    'right:90px',
    'z-index:12',
    'width:32px',
    'height:32px',
    'border-radius:50%',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'font-size:15px',
    'color:rgba(255,255,255,0.45)',
    'background:rgba(0,0,0,0.45)',
    'border:2px solid rgba(255,255,255,0.12)',
    'cursor:pointer',
    'user-select:none',
    'backdrop-filter:blur(6px)',
    'transition:color 0.15s,border-color 0.15s',
  ].join(';');
  globalLbBtn.addEventListener('click', () => globalLeaderboard.toggle());
  document.body.appendChild(globalLbBtn);

  // ── Help toggle ────────────────────────────────────────
  const helpBtn = document.createElement('div');
  helpBtn.id = 'help-btn';
  helpBtn.textContent = '?';
  helpBtn.style.cssText = [
    'position:fixed',
    'top:10px',
    'right:130px',
    'z-index:12',
    'width:32px',
    'height:32px',
    'border-radius:50%',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'font-size:16px',
    'font-weight:900',
    'color:rgba(255,255,255,0.45)',
    'background:rgba(0,0,0,0.45)',
    'border:2px solid rgba(255,255,255,0.12)',
    'cursor:pointer',
    'user-select:none',
    'backdrop-filter:blur(6px)',
    'transition:color 0.15s,border-color 0.15s',
  ].join(';');
  helpBtn.addEventListener('click', () => helpWindow.toggle());
  document.body.appendChild(helpBtn);

  // ── Exit game button ──────────────────────────────────
  const exitBtn = document.createElement('div');
  exitBtn.id = 'exit-btn';
  exitBtn.textContent = '✕';
  exitBtn.title = 'Leave Game';
  exitBtn.style.cssText = [
    'position:fixed',
    'top:10px',
    'right:170px',
    'z-index:12',
    'width:32px',
    'height:32px',
    'border-radius:50%',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'font-size:16px',
    'font-weight:900',
    'color:rgba(255,100,100,0.55)',
    'background:rgba(0,0,0,0.45)',
    'border:2px solid rgba(255,100,100,0.2)',
    'cursor:pointer',
    'user-select:none',
    'backdrop-filter:blur(6px)',
    'transition:color 0.15s,border-color 0.15s,background 0.15s',
  ].join(';');
  exitBtn.addEventListener('mouseenter', () => {
    exitBtn.style.color = 'rgba(255,80,80,0.9)';
    exitBtn.style.borderColor = 'rgba(255,80,80,0.5)';
    exitBtn.style.background = 'rgba(140,0,0,0.35)';
  });
  exitBtn.addEventListener('mouseleave', () => {
    exitBtn.style.color = 'rgba(255,100,100,0.55)';
    exitBtn.style.borderColor = 'rgba(255,100,100,0.2)';
    exitBtn.style.background = 'rgba(0,0,0,0.45)';
  });
  exitBtn.addEventListener('click', () => {
    network.leave();
    window.location.reload();
  });
  document.body.appendChild(exitBtn);

  // ── Bot manager panel ───────────────────────────────────
  const botPanelWrap = document.createElement('div');
  botPanelWrap.id = 'bot-manager';
  botPanelWrap.style.cssText = [
    'position:fixed',
    'top:10px',
    'right:50px',       // sits left of the mute button (32px wide + 8px gap)
    'z-index:12',
    'display:flex',
    'align-items:center',
    'gap:4px',
    'pointer-events:all',
  ].join(';');

  // Toggle button
  const botToggleBtn = document.createElement('div');
  botToggleBtn.style.cssText = [
    'width:32px',
    'height:32px',
    'border-radius:50%',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'font-size:15px',
    'color:rgba(255,255,255,0.45)',
    'background:rgba(0,0,0,0.45)',
    'border:2px solid rgba(255,255,255,0.12)',
    'cursor:pointer',
    'user-select:none',
    'backdrop-filter:blur(6px)',
    'transition:color 0.15s,border-color 0.15s',
  ].join(';');
  botToggleBtn.textContent = '🤖';

  // Expanded controls (hidden by default)
  const botControls = document.createElement('div');
  botControls.style.cssText = [
    'display:none',
    'align-items:center',
    'gap:4px',
  ].join(';');

  // Bot count label
  const botCountLabel = document.createElement('div');
  botCountLabel.style.cssText = [
    'padding:4px 8px',
    'border-radius:12px',
    'font-size:10px',
    'font-weight:800',
    'letter-spacing:0.1em',
    'color:rgba(255,255,255,0.6)',
    'background:rgba(0,0,0,0.45)',
    'border:2px solid rgba(255,255,255,0.12)',
    'white-space:nowrap',
    'backdrop-filter:blur(6px)',
  ].join(';');
  botCountLabel.textContent = 'BOTS: 0';

  function updateBotCount() {
    const state = network.state;
    if (!state) return;
    let count = 0;
    state.machines.forEach((m: any) => { if (m.isBot) count++; });
    botCountLabel.textContent = `BOTS: ${count}`;
  }

  function makeBotBtn(label: string, color: string, borderColor: string, onClick: () => void) {
    const btn = document.createElement('div');
    btn.textContent = label;
    btn.style.cssText = [
      'padding:4px 8px',
      'border-radius:12px',
      'font-size:10px',
      'font-weight:800',
      'letter-spacing:0.08em',
      `color:${color}`,
      'background:rgba(0,0,0,0.45)',
      `border:2px solid ${borderColor}`,
      'cursor:pointer',
      'user-select:none',
      'backdrop-filter:blur(6px)',
      'transition:background 0.15s',
    ].join(';');
    btn.addEventListener('mouseenter', () => btn.style.background = 'rgba(255,255,255,0.08)');
    btn.addEventListener('mouseleave', () => btn.style.background = 'rgba(0,0,0,0.45)');
    btn.addEventListener('click', onClick);
    return btn;
  }

  const removeBtn = makeBotBtn('−',  'rgba(255,100,100,0.8)', 'rgba(255,100,100,0.35)', () => network.removeBot());
  const easyBtn   = makeBotBtn('+E', 'rgba(100,220,100,0.9)', 'rgba(100,220,100,0.35)', () => network.addBot('easy'));
  const mediumBtn = makeBotBtn('+M', 'rgba(255,200,50,0.9)',  'rgba(255,200,50,0.35)',  () => network.addBot('medium'));
  const hardBtn   = makeBotBtn('+H', 'rgba(255,100,100,0.9)', 'rgba(255,100,100,0.35)', () => network.addBot('hard'));

  botControls.append(removeBtn, easyBtn, mediumBtn, hardBtn, botCountLabel);

  let botPanelOpen = false;
  botToggleBtn.addEventListener('click', () => {
    botPanelOpen = !botPanelOpen;
    botControls.style.display = botPanelOpen ? 'flex' : 'none';
    botToggleBtn.style.color = botPanelOpen ? 'rgba(255,200,100,0.9)' : 'rgba(255,255,255,0.45)';
    botToggleBtn.style.borderColor = botPanelOpen ? 'rgba(255,200,100,0.4)' : 'rgba(255,255,255,0.12)';
    if (botPanelOpen) updateBotCount();
  });

  botPanelWrap.append(botControls, botToggleBtn);
  document.body.appendChild(botPanelWrap);

  // Keep bot count in sync whenever state changes
  network.on('stateChange', updateBotCount);

  // ── Camera lock toggle (Shift key) ─────────────────────
  let cameraLocked = true; // true = camera tracks body; false = free orbit
  window.addEventListener('keydown', (e) => {
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
      cameraLocked = !cameraLocked;
      if (cameraLocked) {
        // Snap tank rotation to where the camera was pointing when re-locking
        controller.rotation = camera.getYaw();
      }
    }
    if (e.code === 'KeyF') setAutoFire(!autoFire);
    if (e.code === 'KeyB') debug.toggle();
    // Close leaderboard when upgrade/stats menu opens; release pointer lock for menus
    if (e.code === 'KeyU' || e.code === 'Digit1' || e.code === 'Digit2' || e.code === 'Escape') {
      leaderboard.hide();
      input.exitPointerLock();
    }
  });

  // ── Game loop ──────────────────────────────────────────
  let fireTimer = 0;
  let inputSeq = 0;
  let sendAccumulator = 0;
  const SEND_INTERVAL = 1 / 20; // match server tick rate (20 Hz)

  app.on('update', (dt: number) => {
    // Consume latest server state once per frame (coalesced from stateChange bursts)
    // Skip processing when in waiting room or match ended to avoid stale state
    if (pendingState && !inWaitingRoom && !matchEnded) {
      processNetworkState(pendingState);
      pendingState = null;
    }

    if (!network.connected || hud.isMenuOpen || globalLeaderboard.isOpen || matchEnded || inWaitingRoom) return;

    // Count down local timers between schema sync events
    if (localPowerUpTimer > 0) {
      localPowerUpTimer = Math.max(0, localPowerUpTimer - dt);
      hud.updatePowerUpTimer(localPowerUpType, localPowerUpTimer);
      if (localPowerUpTimer <= 0) localPowerUpType = '';
    }
    if (localFrozenTimer > 0) {
      localFrozenTimer = Math.max(0, localFrozenTimer - dt);
      if (localMachine) localMachine.setFrozen(localFrozenTimer > 0);
    }

    const kbState = input.capture();
    const kbActive = kbState.dx !== 0 || kbState.dz !== 0 || kbState.turn !== 0 || kbState.firing || kbState.orbitV !== 0;
    const inputState: InputState = (kbActive || !touchInput) ? kbState : touchInput.capture(kbState.seq);

    // PlayerController handles: turn (locked mode only), server-correction drain,
    // WASD→world-space transform, movement, and arena clamping — all in one call.
    // In free-cam mode we pass the current camera yaw so WASD stays camera-relative.
    const { worldDX, worldDZ, moveLen } = localMachine
      ? controller.update(inputState, dt, cameraLocked, camera.getYaw())
      : { worldDX: 0, worldDZ: 0, moveLen: 0 };

    if (localMachine) {
      // Camera: in locked mode, force the yaw to sit directly behind the tank.
      // In free mode, the turn input orbits the camera independently.
      if (cameraLocked) {
        // Mouse-look turns the tank body directly in locked mode
        if (input.pointerLocked && inputState.mouseX !== 0) {
          controller.rotation += inputState.mouseX * 0.003;
        }
        camera.setYaw(controller.rotation);
        camera.follow(controller.x, controller.z, 0, inputState.orbitV, moveLen * controller.moveSpeed, dt, 0, inputState.mouseY);
      } else {
        camera.follow(controller.x, controller.z, inputState.turn, inputState.orbitV, moveLen * controller.moveSpeed, dt, inputState.mouseX, inputState.mouseY);
        // Keep tank body facing the camera direction in free mode so switching
        // back to locked mode doesn't produce a jarring body-angle jump.
        controller.rotation = camera.getYaw();
      }

      // PlayCanvas Y-rotation is CCW, but our controller.rotation accumulates CW
      // (turn right = positive). Negate for display so the barrel faces the correct
      // world direction and aligns with bullet velocity (sin/−cos convention).
      localMachine.setPosition(controller.x, controller.z);
      localMachine.setBodyRotation(-controller.rotation);
      localMachine.setTurretAngle(-controller.rotation);

      // Fire — triggers on held Space/Mouse or when auto-fire is toggled on.
      // Timer always drains so rapid clicks cannot bypass the fire rate.
      // When not firing and timer expires, clamp to 0 so the next press is instant.
      fireTimer -= dt;
      if ((inputState.firing || autoFire) && fireTimer <= 0) {
        const weaponDef = UPGRADE_DEFS.weapon[localUpgrades.weapon];
        const ammoRackMult = localUpgrades.attachment === 'ammo_rack' ? 1.5 : 1.0;
        const fireRate = (weaponDef?.fireRate ?? 1.0) * (1 + localFireRateLevel * 0.08) * ammoRackMult;
        fireTimer = 1 / fireRate;
        // Spread values match the server (gameData.ts) per weapon tier
        const spreadByTier = [0, 0.1, 0.4, 0, 0.05, 0];
        const spread = spreadByTier[localUpgrades.weapon] ?? 0;
        const muzzles = localMachine.getMuzzlePositions();
        const count = muzzles.length;
        for (let i = 0; i < count; i++) {
          const spreadOffset = count > 1 ? (i / (count - 1) - 0.5) * spread * 2 : 0;
          const angle = controller.rotation + spreadOffset;
          const muzzle = muzzles[i];
          const bulletVariant = localActivePowerUp === 'fireball' ? 1 : localActivePowerUp === 'iceball' ? 2 : 0;
          bullets.spawn(`local_${inputSeq++}`, muzzle.x, muzzle.y, muzzle.z, Math.sin(angle), -Math.cos(angle), localUpgrades.weapon, 1 + localBulletSpeedLevel * 0.08, bulletVariant);
          particles.muzzleFlash(muzzle.x, muzzle.y, muzzle.z);
        }
        audio.shoot(localUpgrades.weapon);
      } else if (fireTimer < 0) {
        fireTimer = 0; // clamp so the next press still fires instantly
      }
    }

    // Send input to server at 20 Hz — no point sending faster than the server ticks
    sendAccumulator += dt;
    if (sendAccumulator >= SEND_INTERVAL) {
      sendAccumulator -= SEND_INTERVAL;
      network.sendInput({
        dx: worldDX,
        dz: worldDZ,
        turretAngle: controller.rotation,
        firing: inputState.firing || autoFire,
        seq: inputSeq,
      });
    }

    // Interpolate remote machines — all values run through frame-rate-stable lerp
    const lerpK = 1 - Math.exp(-10 * dt); // rate 10 (was 20) — smoother burst recovery
    remoteMachines.forEach((remote, sessionId) => {
      const target  = remoteTargets.get(sessionId);
      const current = remoteCurrent.get(sessionId);
      if (!target || !current) return;

      current.x += (target.x - current.x) * lerpK;
      current.z += (target.z - current.z) * lerpK;

      // Angle lerp with shortest-path wrapping
      let dr = target.rotation - current.rotation;
      while (dr >  Math.PI) dr -= Math.PI * 2;
      while (dr < -Math.PI) dr += Math.PI * 2;
      current.rotation += dr * lerpK;

      let dta = target.turretAngle - current.turretAngle;
      while (dta >  Math.PI) dta -= Math.PI * 2;
      while (dta < -Math.PI) dta += Math.PI * 2;
      current.turretAngle += dta * lerpK;

      remote.setPosition(current.x, current.z);
      remote.setBodyRotation(-current.rotation);
      remote.setTurretAngle(-current.turretAngle);

      // Project name tag to screen — hide when machine is invisible
      const tag = nameTags.get(sessionId);
      if (tag) {
        if (!remote.root.enabled) {
          tag.style.display = 'none';
        } else {
          const screen = camera.worldToScreen(remote.getLabelAnchorPosition());
          if (screen) {
            tag.style.transform = `translate3d(${screen.x}px, ${screen.y}px, 0) translate(-50%, -100%)`;
            tag.style.display = '';
          } else {
            tag.style.display = 'none'; // behind camera
          }
        }
      }
    });

    // Update flag-carrier beacon positions
    carrierBeacons.forEach(({ beacon }, carrierId) => {
      if (carrierId === network.sessionId) {
        beacon.setPosition(controller.x, 0, controller.z);
      } else {
        const cur = remoteCurrent.get(carrierId);
        if (cur) beacon.setPosition(cur.x, 0, cur.z);
      }
    });

    // ── Capture waypoint — show when local player carries a flag ──────
    if (localCarryingFlag && localTeam) {
      // Point to YOUR base (that's where you deliver the enemy flag)
      const home = localTeam === 'red' ? CTF_RED_FLAG_HOME : CTF_BLUE_FLAG_HOME;
      _cwPos.set(home.x, 3, home.z);
      const screen = camera.worldToScreen(_cwPos);
      const color = localTeam === 'red' ? '#ff6060' : '#60a0ff';
      const margin = 50;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const arrowEl = captureWaypoint.querySelector('#cw-arrow') as HTMLElement;

      if (screen && screen.x >= margin && screen.x <= w - margin
                 && screen.y >= margin && screen.y <= h - margin) {
        // On-screen: place the marker at the projected position
        captureWaypoint.style.display = 'flex';
        captureWaypoint.style.color = color;
        captureWaypoint.style.left = `${screen.x}px`;
        captureWaypoint.style.top = `${screen.y}px`;
        captureWaypoint.style.transform = 'translate(-50%, -100%)';
        arrowEl.textContent = '▼';
      } else {
        // Off-screen: clamp to screen edge as directional arrow
        let dirX: number, dirY: number;
        if (screen) {
          // Projected but outside viewport — use screen-space direction from center
          dirX = screen.x - w / 2;
          dirY = screen.y - h / 2;
        } else {
          // Behind camera — project a point slightly toward the base to get a screen direction,
          // then flip it (since the base is behind us, we want to point backward on screen)
          _cwPos.set(
            controller.x + (home.x - controller.x) * 0.01,
            3,
            controller.z + (home.z - controller.z) * 0.01,
          );
          const near = camera.worldToScreen(_cwPos);
          dirX = near ? -(near.x - w / 2) : 0;
          dirY = near ? -(near.y - h / 2) : -1;
        }
        const len = Math.sqrt(dirX * dirX + dirY * dirY) || 1;
        const nx = dirX / len;
        const ny = dirY / len;
        // Project from center along direction to edge
        const edgeX = w / 2 + nx * (w / 2 - margin);
        const edgeY = h / 2 + ny * (h / 2 - margin);
        const clampedX = Math.max(margin, Math.min(w - margin, edgeX));
        const clampedY = Math.max(margin, Math.min(h - margin, edgeY));
        const angle = Math.atan2(nx, -ny);
        captureWaypoint.style.display = 'flex';
        captureWaypoint.style.color = color;
        captureWaypoint.style.left = `${clampedX}px`;
        captureWaypoint.style.top = `${clampedY}px`;
        captureWaypoint.style.transform = `translate(-50%, -50%) rotate(${angle}rad)`;
        arrowEl.textContent = '▲';
      }
    } else {
      captureWaypoint.style.display = 'none';
    }

    // Billboard all HP bars + client-side hit prediction
    const camPos = camera.entity.getPosition();
    localMachine?.billboard(camPos);
    remoteMachines.forEach(remote => {
      bullets.forEachActive((bx, bz) => remote.peekBullet(bx, bz));
      remote.billboard(camPos);
    });
    destructibles.forEach((ent) => {
      // Client-side prediction: reveal HP bar the instant a local bullet is in range
      bullets.forEachActive((bx, bz) => ent.peekBullet(bx, bz));
      ent.update(camPos);
    });
    powerUps.forEach(pu => pu.update(dt, camPos));
    portals.forEach(p => p.update(dt));

    // Debug hitbox overlay (toggle with B)
    debug.update(controller.x, controller.z, remoteMachines, destructibles, bullets);

    // Update bullets
    bullets.update(dt);

    // Update particles
    particles.update(dt);
  });
}

main().catch(console.error);
